// Pure helpers for computing which picks to clear when a user's match pick
// changes. Extracted from useBracketStore so the invariants can be tested
// directly without spinning up the full store.

/** Find the match that a given match feeds into, or null if none. */
export function findNextMatch(currentMatch, allMatches) {
  if (!currentMatch) return null
  if (currentMatch.round === 0) {
    if (currentMatch.feeds_into_round && currentMatch.feeds_into_match_number) {
      return allMatches.find(m =>
        m.round === currentMatch.feeds_into_round &&
        m.match_number === currentMatch.feeds_into_match_number
      ) || null
    }
    return null
  }
  const nextRound = currentMatch.round + 1
  const nextMatchNumber = Math.ceil(currentMatch.match_number / 2)
  return allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber) || null
}

/**
 * When the winner of `match` changes (user view, not official), figure out
 * which downstream match_ids need their picks cleared.
 *
 *  - The IMMEDIATE downstream pick is cleared unconditionally: one of its
 *    opponents just changed, so the previous pick is against a stale matchup
 *    (DUB-45).
 *  - Further downstream picks cascade only where the team that was advancing
 *    from the cleared match still appears — so a user's pick of an unrelated
 *    team deeper in the bracket is preserved.
 *
 * Returns the list of match_ids to clear, ordered from nearest to furthest.
 */
export function computeDownstreamClears(match, allMatches, picks) {
  const clears = []
  const nextMatch = findNextMatch(match, allMatches)
  if (!nextMatch) return clears

  const nextPick = picks[nextMatch.match_id]
  const teamThatWasAdvancing = nextPick?.teamId || null
  if (!teamThatWasAdvancing) return clears

  clears.push(nextMatch.match_id)

  let cursor = nextMatch
  let teamToClear = teamThatWasAdvancing
  while (true) {
    const next = findNextMatch(cursor, allMatches)
    if (!next) break
    const p = picks[next.match_id]
    if (!p || p.teamId !== teamToClear) break
    clears.push(next.match_id)
    cursor = next
    // teamToClear stays the same — we're tracing the path of the same team.
  }
  return clears
}
